/**
 * This file contains helper functions and type declarations to fix common TypeScript errors.
 */

// Fix for "Property 'blur' does not exist on type 'Element'"
declare global {
  interface Element {
    blur(): void;
  }
}

// Safe property access helper for objects that might be arrays
export function safePropertyAccess<T, K extends string>(
  obj: T | { [key in K]?: any }[] | null | undefined,
  property: K,
  defaultValue: any = undefined
): any {
  if (!obj) return defaultValue;
  
  // If it's potentially an array but we're trying to access properties directly
  if (Array.isArray(obj)) {
    return defaultValue;
  }
  
  // Otherwise, access the property safely
  return (obj as any)[property] !== undefined ? (obj as any)[property] : defaultValue;
}

// Type guard to safely check if an error object has a response property
export function hasResponse(error: unknown): error is { response: unknown } {
  return typeof error === 'object' && error !== null && 'response' in error;
}

// Type guard to safely check if an error object has a message property
export function hasMessage(error: unknown): error is { message: string } {
  return typeof error === 'object' && error !== null && 'message' in error;
}

// Type guard to safely check if an error object has a stack property
export function hasStack(error: unknown): error is { stack: string } {
  return typeof error === 'object' && error !== null && 'stack' in error;
}

// Helper for safely getting an error message
export function getErrorMessage(error: unknown): string {
  if (hasMessage(error)) {
    return error.message;
  }
  return String(error);
}

// Add any other type fixes as needed

export default {}; 